import { useState, useMemo } from 'react';
import style from './PostCreate.module.css';
import { uploadFile } from '../../../api/filesApi';
import { createPost } from '../../../api/postsApi';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

/**
 * Форма створення поста.
 * Повертає об'єкт { title, description, keyWords, mainText, imageUrl }
 * через колбек onCreate(obj) або виводить у console.log, якщо колбек не передано.
 */
const PostCreate = ({ onCreate, defaultValues }) => {
  const [files, setFiles] = useState([]);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: defaultValues?.title || '',
    description: defaultValues?.description || '',
    keyWords: defaultValues?.keyWords || '',
    mainText: defaultValues?.mainText || '',
    imageUrl: defaultValues?.imageUrl || '',
  });

  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.mainText.trim()) e.mainText = 'Text is required';
    if (files[0]) {
      e.imageUrl = 'Посилання має починатися з http(s)://';
    }
    return e;
  }, [form]);

  const handleChangeFile = e => {
    setFiles([...e.target.files]);
  };

  const isValid = Object.keys(errors).length === 0;

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(s => ({ ...s, [name]: value }));
  };

  const handleBlur = e => {
    const { name } = e.target;
    setTouched(t => ({ ...t, [name]: true }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // позначити всі як торкнуті, щоб показати помилки
    setTouched({
      title: true,
      description: true,
      keyWords: true,
      mainText: true,
      imageUrl: true,
    });
    if (!isValid) return;

    const file = files[0];
    const imageUrl = await uploadFile(file);

    const result = {
      title: form.title.trim(),
      description: form.description.trim(),
      keyWords: form.keyWords.trim(), // залишаємо як рядок; за потреби можна split(',')
      mainText: form.mainText.trim(),
      imageUrl: imageUrl,
    };

    if (typeof onCreate === 'function') {
      onCreate(result);
    } else {
      const promise = createPost(result);
      toast
        .promise(promise, {
          loading: 'Wait. Post is creating',
          success: 'Post has been created',
          error: 'Ups. Something went wrong',
        })
        .then(() => {
          navigate('/admin/posts');
        });
    }

    // опціонально очищаємо форму
    setForm({
      title: '',
      description: '',
      keyWords: '',
      mainText: '',
      imageUrl: '',
    });
  };

  const CharCounter = ({ value, max }) => (
    <span className={style.counter} aria-live="polite">
      {value.length}
      {typeof max === 'number' ? `/${max}` : ''}
    </span>
  );

  return (
    <div className={style.wrapper}>
      <form className={style.form} onSubmit={handleSubmit} noValidate>
        <h2 className={style.title}>Create Post</h2>

        {/* Title */}
        <div className={style.group}>
          <label className={style.label} htmlFor="title">
            Title *
          </label>
          <input
            id="title"
            name="title"
            type="text"
            className={style.input}
            placeholder="Type Title"
            value={form.title}
            onChange={handleChange}
            onBlur={handleBlur}
            required
          />
          <div className={style.metaRow}>
            <CharCounter value={form.title} max={120} />
            {touched.title && errors.title && (
              <span className={style.error}>{errors.title}</span>
            )}
          </div>
        </div>

        {/* Description */}
        <div className={style.group}>
          <label className={style.label} htmlFor="description">
            Meta Description
          </label>
          <textarea
            id="description"
            name="description"
            className={style.textarea}
            placeholder="Meta Description"
            value={form.description}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={3}
          />
          <div className={style.metaRow}>
            <CharCounter value={form.description} max={300} />
          </div>
        </div>

        {/* KeyWords */}
        <div className={style.group}>
          <label className={style.label} htmlFor="keyWords">
            Meta Keywords
          </label>
          <input
            id="keyWords"
            name="keyWords"
            type="text"
            className={style.input}
            placeholder="Meta keywords"
            value={form.keyWords}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <div className={style.metaRow}>
            <small className={style.hint}>
              Залишаємо рядком. За потреби можна перетворити на масив при
              відправці.
            </small>
          </div>
        </div>

        {/* Main Text */}
        <div className={style.group}>
          <label className={style.label} htmlFor="mainText">
            Main Text *
          </label>
          <textarea
            id="mainText"
            name="mainText"
            className={style.textarea}
            placeholder="Type the article"
            value={form.mainText}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={8}
            required
          />
          <div className={style.metaRow}>
            <CharCounter value={form.mainText} />
            {touched.mainText && errors.mainText && (
              <span className={style.error}>{errors.mainText}</span>
            )}
          </div>
        </div>

        {/* Image URL */}
        <div className={style.group}>
          <label className={style.label} htmlFor="imageUrl">
            Upload Image
          </label>
          <input
            id="imageUrl"
            name="imageUrl"
            type="file"
            className={style.input}
            onChange={handleChangeFile}
            onBlur={handleBlur}
          />
        </div>

        <div className={style.actions}>
          <button type="submit" className={style.submitBtn} disabled={!isValid}>
            Create Post
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostCreate;
